import React from "react";
import {
  getSignPdfSetupChecklist,
  SIGN_PDF_WORKFLOW_STEPS,
  SIGN_PDF_WORKFLOW_STEP_LABELS,
  signPdfWorkflowHint,
} from "./signPdfWorkflow";
import { getSignPdfPageScope, SIGN_PDF_INTENT_OPTIONS, SIGN_PDF_MODES, signPdfModeFromSettings } from "./signPdfSettings";
import {
  getSignPdfTemplate,
  listSignPdfPlacements,
  SIGN_PDF_FIELD_KINDS,
  SIGN_PDF_FIELD_OPTIONS,
} from "./signPdfPlacements";
import "./SignPdfSetupPanel.css";

const SIGN_PDF_MODE_DISPLAY_LABELS = {
  [SIGN_PDF_MODES.TYPE]: "Text signature",
  [SIGN_PDF_MODES.UPLOAD]: "Image signature",
  [SIGN_PDF_MODES.DRAW]: "Drawn signature",
};

function formatSignPdfFieldsUsedLabel(session) {
  const kinds = new Set(listSignPdfPlacements(session).map((p) => p.kind));
  const labels = SIGN_PDF_FIELD_OPTIONS.filter((o) => kinds.has(o.id)).map(
    (o) => o.label.toLowerCase()
  );
  return labels.length ? labels.join(", ") : null;
}

/**
 * Left-column guided status — visible from first open (upload + steps).
 */
const SignPdfSetupPanel = ({
  session,
  workflowStep = SIGN_PDF_WORKFLOW_STEPS.UPLOAD,
  reviewComplete = false,
  currentPage = 1,
  hasDocument = false,
  pageCount = 0,
  fileSizeLabel = "",
  disabled = false,
  onEditSignature = () => {},
  onChooseFormat = () => {},
  onOpenFinish = () => {},
}) => {
  const checklist = getSignPdfSetupChecklist(session);
  const scope = getSignPdfPageScope(session);
  const scopeLabel =
    SIGN_PDF_INTENT_OPTIONS.find((o) => o.id === scope)?.label || "Same on all pages";
  const hint = signPdfWorkflowHint(workflowStep, session, { currentPage });
  const whoChosen = Boolean(session?.signPdfWhoChosen);
  const whoLabel = whoChosen ? "Sign myself" : null;
  const formatLabel = checklist.format.done ? scopeLabel : null;
  const signatureTemplate = getSignPdfTemplate(
    session,
    SIGN_PDF_FIELD_KINDS.SIGNATURE
  );
  const signatureModeLabel = checklist.signatureReady
    ? SIGN_PDF_MODE_DISPLAY_LABELS[signPdfModeFromSettings(signatureTemplate)] ||
      "Text signature"
    : null;

  const fieldsUsedLabel = formatSignPdfFieldsUsedLabel(session);
  const hasPlacedFields = Boolean(fieldsUsedLabel);

  const uploadLabel = hasDocument
    ? [
        "PDF document",
        pageCount > 0
          ? `${pageCount} page${pageCount === 1 ? "" : "s"}`
          : null,
        fileSizeLabel || null,
      ]
        .filter(Boolean)
        .join(" - ")
    : null;

  const steps = [
    {
      id: SIGN_PDF_WORKFLOW_STEPS.UPLOAD,
      title: hasDocument ? "Document uploaded" : "Upload your document",
      hint: "Drop a PDF or click the upload area above.",
      selectedLabel: uploadLabel,
      done: hasDocument,
      active: workflowStep === SIGN_PDF_WORKFLOW_STEPS.UPLOAD,
    },
    {
      id: SIGN_PDF_WORKFLOW_STEPS.WHO,
      title: "Who is signing",
      hint: "Choose who is signing when prompted after upload.",
      selectedLabel: whoLabel,
      done: whoChosen,
      active: workflowStep === SIGN_PDF_WORKFLOW_STEPS.WHO,
      pending: !hasDocument,
    },
    {
      id: SIGN_PDF_WORKFLOW_STEPS.FORMAT,
      title: "Signing format",
      hint: "Same on all pages, one page, or different per page.",
      selectedLabel: formatLabel,
      done: checklist.format.done,
      active: workflowStep === SIGN_PDF_WORKFLOW_STEPS.FORMAT,
      pending: !whoChosen,
      action: onChooseFormat,
      actionLabel: checklist.format.done ? "Change format" : "Choose format",
    },
    {
      id: SIGN_PDF_WORKFLOW_STEPS.SETUP,
      title: checklist.signatureReady ? "Signature Created" : "Create your signature",
      hint: "Required before placing anything on the document.",
      selectedLabel: signatureModeLabel,
      done: checklist.signatureReady,
      active: workflowStep === SIGN_PDF_WORKFLOW_STEPS.SETUP,
      pending: !checklist.format.done,
      action: onEditSignature,
      actionLabel: checklist.signatureReady ? "Edit signature" : "Create signature",
    },
    {
      id: SIGN_PDF_WORKFLOW_STEPS.PLACE,
      title: hasPlacedFields ? "Fields used on this document" : "Place fields",
      hint: "Use Signature Fields or Signature blocks in the toolbar above the preview.",
      selectedLabel: fieldsUsedLabel,
      done:
        workflowStep === SIGN_PDF_WORKFLOW_STEPS.REVIEW ||
        workflowStep === SIGN_PDF_WORKFLOW_STEPS.FINISH,
      active: workflowStep === SIGN_PDF_WORKFLOW_STEPS.PLACE,
      pending: !checklist.complete,
    },
    {
      id: SIGN_PDF_WORKFLOW_STEPS.REVIEW,
      title: "Review & finish",
      hint: "Check every page, then download or share.",
      done: reviewComplete,
      active:
        (workflowStep === SIGN_PDF_WORKFLOW_STEPS.REVIEW ||
          workflowStep === SIGN_PDF_WORKFLOW_STEPS.FINISH) &&
        !reviewComplete,
      pending: !checklist.complete,
      action: onOpenFinish,
      actionLabel: "Download / share",
    },
  ];

  return (
    <div className="sp-setup-panel">
      <p className="sp-setup-panel__status">
        <strong>{SIGN_PDF_WORKFLOW_STEP_LABELS[workflowStep] || "Sign PDF"}</strong>
        {" — "}
        {hint}
        {checklist.format.done ? (
          <>
            {" "}
            <span style={{ color: "#6f6f6f" }}>({scopeLabel})</span>
          </>
        ) : null}
      </p>

      {steps.map((step, idx) => {
        const state = step.pending
          ? "pending"
          : step.active
            ? "active"
            : step.done
              ? "done"
              : "";
        const showAction =
          step.action &&
          !step.pending &&
          (step.id === SIGN_PDF_WORKFLOW_STEPS.REVIEW
            ? reviewComplete
            : step.actionAlways || (step.active && !step.done));
        return (
          <div
            key={step.id}
            className={`sp-setup-panel__step${state ? ` is-${state}` : ""}`}
          >
            <span className="sp-setup-panel__step-num" aria-hidden="true">
              {step.done ? "✓" : idx + 1}
            </span>
            <div className="sp-setup-panel__step-body">
              <p className="sp-setup-panel__step-title">{step.title}</p>
              {step.selectedLabel ? (
                <p className="sp-setup-panel__step-choice">{step.selectedLabel}</p>
              ) : null}
              {!step.selectedLabel ? (
                <p className="sp-setup-panel__step-hint">{step.hint}</p>
              ) : null}
              {showAction ? (
                <button
                  type="button"
                  className={`sp-setup-panel__cta${
                    step.id === SIGN_PDF_WORKFLOW_STEPS.REVIEW
                      ? " sp-setup-panel__cta--finish"
                      : ""
                  }`}
                  disabled={disabled}
                  onClick={step.action}
                >
                  {step.actionLabel}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SignPdfSetupPanel;
